import React, { useEffect, useState } from 'react';
import axios from 'axios';
import TrainingTimeFields from '../components/TrainingTimeFields';
import { TEST_TYPES, parseTrainingTime, formatTrainingRequirement } from '../utils/trainingGoal';
import './PhysicalExerciseManager.css';
const empty = {
  name: '',
  icon: '',
  gender: 'both',
  testType: 'repetitions',
  minimumResult: '',
  testDurationSeconds: null,
  instructions: '',
  displayOrder: 0
};
export default function PhysicalExerciseManager({
  apiUrl,
  courseId
}) {
  const [exercises, setExercises] = useState([]),
    [form, setForm] = useState({
      ...empty
    });
  const [editing, setEditing] = useState(null),
    [minutes, setMinutes] = useState(''),
    [seconds, setSeconds] = useState('');
  const [holdMinutes, setHoldMinutes] = useState(''),
    [holdSeconds, setHoldSeconds] = useState('');
  const [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${apiUrl}adm/physicalExercises/${courseId}`).then(({
      data
    }) => {
      if (alive) setExercises(data.exercises);
    }).catch(() => {
      if (alive) setError('Não foi possível carregar os exercícios.');
    }).finally(() => {
      if (alive) setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [apiUrl, courseId, revision]);
  const change = (field, value) => setForm(f => ({
    ...f,
    [field]: value
  }));
  const reset = () => {
    setForm({
      ...empty
    });
    setEditing(null);
    setMinutes('');
    setSeconds('');
    setHoldMinutes('');
    setHoldSeconds('');
  };
  const body = () => {
    const minimum = form.testType === 'isometric_hold' ? parseTrainingTime(holdMinutes, holdSeconds) : Number(form.minimumResult);
    if (!(minimum > 0)) throw new Error('Informe o mínimo exigido pelo edital.');
    if (form.testType !== 'distance_fixed_time' && !Number.isInteger(minimum)) throw new Error('Repetições e segundos devem ser inteiros.');
    const duration = TEST_TYPES[form.testType].timed ? parseTrainingTime(minutes, seconds) : null;
    if (TEST_TYPES[form.testType].timed && !duration) throw new Error('Informe a duração completa do teste.');
    return {
      ...form,
      name: form.name.trim(),
      icon: form.icon.trim() || null,
      minimumResult: minimum,
      testDurationSeconds: duration,
      displayOrder: editing ? form.displayOrder : exercises.length
    };
  };
  let preview;
  try {
    const data = body();
    preview = formatTrainingRequirement({
      test_type: data.testType,
      minimum_result: data.minimumResult,
      test_duration_seconds: data.testDurationSeconds
    });
  } catch {
    preview = 'Preencha a exigência para visualizar a meta do aluno.';
  }
  const save = async event => {
    event.preventDefault();
    setError('');
    setNotice('');
    let data;
    try {
      data = body();
    } catch (e) {
      setError(e.message);
      return;
    }
    setBusy(true);
    try {
      if (editing) await axios.put(`${apiUrl}adm/physicalExercises/${editing}`, data);else await axios.post(`${apiUrl}adm/physicalExercises`, {
        ...data,
        courseId: Number(courseId)
      });
      setNotice(editing ? 'Exercício atualizado. Registros anteriores preservam a meta original.' : 'Exercício cadastrado.');
      reset();
      setRevision(r => r + 1);
    } catch (e) {
      setError(e.response?.data?.errors?.map(issue => issue.message).join(' ') || e.response?.data?.message || 'Não foi possível salvar.');
    } finally {
      setBusy(false);
    }
  };
  const edit = exercise => {
    setEditing(exercise.id);
    setError('');
    setNotice('');
    setForm({
      name: exercise.name,
      icon: exercise.icon || '',
      gender: exercise.gender,
      testType: exercise.test_type,
      minimumResult: String(exercise.minimum_result),
      testDurationSeconds: exercise.test_duration_seconds,
      instructions: exercise.instructions || '',
      displayOrder: exercise.display_order
    });
    setMinutes(exercise.test_duration_seconds ? String(Math.floor(exercise.test_duration_seconds / 60)) : '');
    setSeconds(exercise.test_duration_seconds ? String(exercise.test_duration_seconds % 60) : '');
    setHoldMinutes(exercise.test_type === 'isometric_hold' ? String(Math.floor(exercise.minimum_result / 60)) : '');
    setHoldSeconds(exercise.test_type === 'isometric_hold' ? String(exercise.minimum_result % 60) : '');
  };
  const toggle = async (exercise, enabled) => {
    setBusy(true);
    setError('');
    try {
      await axios.put(`${apiUrl}adm/physicalExercises/${exercise.id}/enabled`, {
        is_enabled: enabled
      });
      setRevision(r => r + 1);
    } catch {
      setError('Não foi possível alterar a disponibilidade.');
    } finally {
      setBusy(false);
    }
  };
  return <section className="admin-card physical-exercise-manager" aria-label="Cadastro de testes do TAF">
    <h3>Treino físico — exigências do edital</h3><p>Cadastre a exigência exata de cada teste e público. Use registros separados para metas diferentes.</p>
    {error && <p role="alert" className="pem-feedback">{error}</p>}{notice && <p role="status" className="pem-feedback">{notice}</p>}
    <form onSubmit={save}><fieldset disabled={busy}>
      <h4>{editing ? 'Editar exercício' : 'Novo exercício'}</h4>
      <label>Nome do exercício<input required maxLength={255} value={form.name} onChange={e => change('name', e.target.value)} placeholder="Ex.: Corrida de resistência" /></label>
      <div className="pem-grid"><label>Ícone (opcional)<input maxLength={50} value={form.icon} onChange={e => change('icon', e.target.value)} /></label>
        <label>Público<select value={form.gender} onChange={e => change('gender', e.target.value)}><option value="both">Todos</option><option value="male">Masculino</option><option value="female">Feminino</option></select></label></div>
      <label>Tipo de teste<select value={form.testType} onChange={e => {
            change('testType', e.target.value);
            change('minimumResult', '');
            setMinutes('');
            setSeconds('');
            setHoldMinutes('');
            setHoldSeconds('');
          }}>{Object.entries(TEST_TYPES).map(([value, t]) => <option key={value} value={value}>{t.label}</option>)}</select></label>
      <p className="pem-hint">{TEST_TYPES[form.testType].example}</p>
      {form.testType === 'isometric_hold' ? <TrainingTimeFields prefix="Tempo mínimo sustentado" minutes={holdMinutes} seconds={holdSeconds} onMinutes={setHoldMinutes} onSeconds={setHoldSeconds} /> : <label>{form.testType === 'distance_fixed_time' ? 'Distância mínima (metros)' : 'Mínimo de repetições'}<input type="number" required min={form.testType === 'distance_fixed_time' ? '0.01' : '1'} max="99999999.99" step={form.testType === 'distance_fixed_time' ? '0.01' : '1'} value={form.minimumResult} onChange={e => change('minimumResult', e.target.value)} /></label>}
      {TEST_TYPES[form.testType].timed && <div><h4>{form.testType === 'distance_fixed_time' ? 'Tempo máximo para a distância' : 'Duração fixa do teste'}</h4><TrainingTimeFields prefix="Duração do teste" minutes={minutes} seconds={seconds} onMinutes={setMinutes} onSeconds={setSeconds} /></div>}
      <label>Orientações do edital (opcional)<textarea maxLength={5000} rows={3} value={form.instructions} onChange={e => change('instructions', e.target.value)} placeholder="Como executar e quais repetições são válidas" /></label>
      <div className="pem-preview"><small>Como o aluno verá</small><strong>{form.name || 'Nome do exercício'}</strong><span>{preview}</span></div>
      <div className="pem-actions"><button type="submit" className="admin-btn-primary">{busy ? 'Salvando…' : editing ? 'Salvar alterações' : 'Cadastrar exercício'}</button>{editing && <button type="button" className="admin-btn-secondary" onClick={reset}>Cancelar edição</button>}</div>
    </fieldset></form>
    <h4>Exercícios cadastrados</h4>{loading ? <p role="status">Carregando exercícios…</p> : !exercises.length ? <p>Nenhum exercício cadastrado neste concurso.</p> : <ul className="pem-list">{exercises.map(ex => <li key={ex.id}>
      <strong>{ex.icon} {ex.name}</strong><span>{formatTrainingRequirement(ex)}</span><small>{ex.gender === 'both' ? 'Todos' : ex.gender === 'male' ? 'Masculino' : 'Feminino'} · {ex.is_enabled ? 'Disponível' : 'Desabilitado'}</small>
      <div className="pem-actions"><button type="button" disabled={busy} className="admin-btn-secondary" onClick={() => edit(ex)}>Editar {ex.name}</button><label className="pem-enabled"><input type="checkbox" disabled={busy} checked={!!ex.is_enabled} onChange={e => toggle(ex, e.target.checked)} /> Disponível para registro</label></div>
    </li>)}</ul>}
  </section>;
}
